import { supabase } from "@/lib/supabase";
import { ORG_IMAGES_BUCKET, type OrgOpenQuest, type PostedAsOrg } from "@/lib/organizations";

/** An org image path → its public URL in the org-images bucket. */
export function orgImageUrl(path: string | null): string | null {
  return path ? supabase.storage.from(ORG_IMAGES_BUCKET).getPublicUrl(path).data.publicUrl : null;
}

/**
 * The "posted as" embed on a quests select. Directory columns only: anon
 * holds column grants on exactly these (20261007100000), so a wider list
 * would fail every logged-out card query.
 */
export const POSTED_AS_ORG_EMBED =
  "organization:organizations!quests_organization_id_fkey(slug, name, logo_path, verified_at)";

export interface PostedAsOrgRow {
  slug: string;
  name: string;
  logo_path: string | null;
  verified_at: string | null;
}

/** The org embed → the card's poster; null when posted as a person (or unreadable). */
export function mapPostedAsOrg(row: PostedAsOrgRow | null | undefined): PostedAsOrg | null {
  if (!row?.slug || !row.name) return null;
  return {
    slug: row.slug,
    name: row.name,
    logoUrl: orgImageUrl(row.logo_path),
    verified: !!row.verified_at,
  };
}

export const ORG_OPEN_QUEST_COLUMNS =
  "id, title, status, deadline, total_budget_usd, currency, created_at, source_lang, i18n, template_id, quest_templates:template_id(status)";

export interface OrgOpenQuestRow {
  id: string;
  title: string;
  status: string;
  deadline: string | null;
  total_budget_usd: number | string;
  currency: string;
  created_at: string;
  source_lang: string | null;
  i18n: OrgOpenQuest["i18n"];
  template_id: string | null;
  quest_templates: { status: string } | null;
}

/**
 * A plain quest is open by its status (filtered in the query). A stock
 * listing's anchor stays `published` after its last slot fills — only its
 * template turns `filled` — so it is open only while the template is. Anon
 * can't read a filled template (quest_templates_select_anon), so a null
 * embed on a listing means "not open" too.
 */
export function isOpenOrgQuestRow(r: OrgOpenQuestRow): boolean {
  return r.template_id === null || r.quest_templates?.status === "open";
}

export function mapOrgOpenQuestRow(r: OrgOpenQuestRow): OrgOpenQuest {
  return {
    id: r.id,
    title: r.title,
    status: r.status,
    deadline: r.deadline,
    totalBudgetUsd: Number(r.total_budget_usd),
    currency: r.currency,
    createdAt: r.created_at,
    sourceLang: r.source_lang,
    i18n: r.i18n,
  };
}
